import { __assign, __spreadArray } from "tslib";
import { useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { createLayout } from 'pro-gallery-layouter';
import ddt from '@wix/da-ddt';
import { useDeepEqualMemo } from '@wix/da-hooks/pkg/useDeepEqualMemo';
import { defaultLayoutConfig, defaultLayoutConfig_desktop_torpedo_310, } from '../../constants';
import { applyMassiveThumbThreshold, applyTinyThumbThreshold, getItemSpacing, getRowSize, } from '../helpers';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import performance from '../../../utils/performance';
var logger = ddt.createLogger('torpedo');
export function useTorpedoLayout(_a) {
    var testRecalculateLayoutSpy = _a.testRecalculateLayoutSpy, _b = _a.items, items = _b === void 0 ? [] : _b, container = _a.container, layoutConfigProp = _a.layoutConfig, useCardLayout = _a.useCardLayout, maxRows = _a.maxRows, hideIncompleteRows = _a.hideIncompleteRows;
    var isMobile = useContext(MobileContext);
    var isInitialRun = useRef(true);
    var desktop_torpedo_310 = useFlag('desktop_torpedo_310');
    // #region memoize all the things
    // this is to ensure the layout only recalculates when something actually relevant changed
    var layoutConfig = useDeepEqualMemo(__assign(__assign({}, (desktop_torpedo_310
        ? defaultLayoutConfig_desktop_torpedo_310
        : defaultLayoutConfig)), layoutConfigProp));
    var styleParams = useMemo(function () {
        var _a, _b;
        return ({
            isColumnsLayout: false,
            allowedGroupTypes: ['1'],
            rowSize: (_a = getRowSize(layoutConfig, isMobile, useCardLayout)) !== null && _a !== void 0 ? _a : 270,
            itemSpacing: (_b = getItemSpacing(layoutConfig, isMobile, useCardLayout)) !== null && _b !== void 0 ? _b : 4,
        });
    }, [isMobile, layoutConfig, useCardLayout]);
    // Items get memoized a bit specially. We do not want to recalc layout if the sizes
    // don't change, but we still want to let the content update if it needs to. So we memoize the
    // size and id only here; we will re-construct the items after the layout is calculated
    var layoutedItems = useDeepEqualMemo(
    // Modify items applying small and massive rules
    items.map(function (_a) {
        var id = _a.id, width = _a.width, height = _a.height;
        return applyTinyThumbThreshold(applyMassiveThumbThreshold({ id: id, width: width, height: height } // clone so we dont mess up references
        ), styleParams.rowSize);
    }));
    // #endregion
    // the main layout math. this is expensive, and only re-runs when the container width;
    // the item sizes; or the style params change
    var calculateLayout = useCallback(function () {
        var t0 = performance.now();
        var newLayout = createLayout({
            items: layoutedItems,
            styleParams: styleParams,
            container: {
                width: container.width,
            },
        });
        var t1 = performance.now();
        logger.log("created layout (".concat(t1 - t0, "ms)"), styleParams, container.width, layoutedItems.length);
        return newLayout;
    }, [layoutedItems, styleParams, container.width]);
    // store the layout in state so updating it triggers a re-render
    var _c = useState(calculateLayout), layout = _c[0], setLayout = _c[1];
    // slice and dice the calculated layout for special needs
    var rows = useMemo(function () {
        if (!layout.strips) {
            return [];
        }
        var strips = layout.strips
            .slice(0, maxRows || undefined)
            // When `hideIncompleteRows` flag is on, don't render rows that do not
            // fill the entire width, except for the first one.
            // This is to avoid having a last row with a few lone items.
            .filter(function (strip, index) {
            return !hideIncompleteRows || strip.isFullWidth || index === 0;
        })
            // flatten structure and merge back the original items
            .map(function (strip) { return (__assign(__assign({}, strip), { items: strip.groups.reduce(function (flatItems, group) { return __spreadArray(__spreadArray([], flatItems, true), group.items, true); }, []) })); });
        return strips;
    }, [layout.strips, hideIncompleteRows, maxRows]);
    // this effect basically just updates the state with the new layout whenever the callback rebinds
    // (and that only happens when the above mentioned deps change)
    useEffect(function () {
        // on initial mount, the state init already calculated our layout, so we can skip that
        if (isInitialRun.current) {
            isInitialRun.current = false;
            return;
        }
        setLayout(calculateLayout());
    }, [calculateLayout]);
    // callback for tests...
    // TODO: see if there is a better way to do this
    useEffect(function () { return testRecalculateLayoutSpy && testRecalculateLayoutSpy(layout); }, [layout, testRecalculateLayoutSpy]);
    return {
        rows: rows,
        styleParams: styleParams,
    };
}
//# sourceMappingURL=useTorpedoLayout.js.map